import { makeStyles } from '@mui/styles';
import {
  CancelBackButton,
  FormSubmitButton,
  TextEditButton,
} from 'Components/Form';
import { SecureComponent } from 'Components/Security';
import { ActionsDropdownConfig, DropdownType, Referral } from 'Models';
import React, { Fragment, HTMLProps, ReactNode } from 'react';
import { CaseStatus, fullClassName, toggledClass, useIdParam } from 'Utilities';
import { ActionsDropdown } from './ActionsDropdown';
import { HeaderBulletedList } from './HeaderBulletedList';
import './PageHeaders.scss';
import { Render } from './Render';
import {
  SecondaryNavigationPillBar,
  SecondaryNavigationPillBarV3,
} from 'Components/Navigation';

import conditions from 'Assets/Icons/conditions_dropdown.svg';
import heapSnapdshotLarge from 'Assets/Icons/heap_snapshot_large_dropdown.svg';
import engineering from 'Assets/Icons/engineering_dropdown.svg';
import pharmacy from 'Assets/Icons/local_pharmacy_dropdown.svg';

import bodySystem from 'Assets/Icons/body_system_dropdown.svg';
import cardiology from 'Assets/Icons/cardiology_dropdown.svg';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { PageCard, PageCardSection } from 'Components/Display';
import {
  faUserMd,
  faBusinessTime,
  faBuilding,
  faUserCog,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row } from 'reactstrap';
import { useEntity, useGlobalContext } from 'Context';
import { Tooltip } from '@mui/material';

type PageHeaderProps = HTMLProps<HTMLDivElement> & {
  title?: string;
  dropdownConfig?: ActionsDropdownConfig;
  tall?: boolean;
};

export const PrimaryHeader: React.FC<PageHeaderProps> = ({
  children,
  title,
  dropdownConfig,
  tall,
  className,
}) => {
  return (
    <div
      className={fullClassName(
        'full-width flex-vertical-center bg-primary-accent page-header page-header-primary',
        toggledClass('tall', tall),
        className
      )}
    >
      {title && <h1>{title}</h1>}
      {children}
      {dropdownConfig && <ActionsDropdown {...dropdownConfig} />}
    </div>
  );
};

export type TaskHeaderProps = PageHeaderProps & {
  saveButtonText?: string;
  disableSaveButton?: boolean;
  customButtons?: ReactNode;
  tall?: boolean;
};

export const TaskHeader: React.FC<TaskHeaderProps> = ({
  children,
  title,
  saveButtonText = 'Save',
  disableSaveButton,
  customButtons,
  className,
  tall,
}) => {
  return (
    <LightPageHeader tall={tall} className={className}>
      <div className="flex-grow">
        {title && <h1>{title}</h1>}
        {children}
      </div>
      {customButtons || (
        <Fragment>
          <CancelBackButton className="mr-3" />
          <FormSubmitButton disabled={disableSaveButton}>
            {saveButtonText}
          </FormSubmitButton>
        </Fragment>
      )}
    </LightPageHeader>
  );
};

export type BulletedHeadingProps = {
  mainBulletItems: (string | undefined)[];
  subBulletItems?: (string | undefined)[];
  smallerMainBullets?: boolean;
};

export const BulletedHeading: React.FC<BulletedHeadingProps> = ({
  mainBulletItems,
  subBulletItems = [],
  smallerMainBullets,
}) => {
  const MainBulletWrapper = smallerMainBullets ? 'h2' : 'h1';

  return (
    <div className="flex-column sub-header-text flex-grow">
      <div className="flex-normal">
        <MainBulletWrapper>
          <HeaderBulletedList header list={mainBulletItems} />
        </MainBulletWrapper>
      </div>
      <HeaderBulletedList list={subBulletItems} />
    </div>
  );
};

export type LightPageHeaderProps = HTMLProps<HTMLDivElement> & {
  tall?: boolean;
  noBorder?: boolean;
};

export const LightPageHeader: React.FC<LightPageHeaderProps> = ({
  tall,
  className,
  children,
  noBorder,
}) => {
  return (
    <div
      className={fullClassName(
        'full-width flex-vertical-center page-header task-page-header bg-light',
        toggledClass('tall', tall),
        toggledClass('no-border', noBorder),
        className
      )}
    >
      {children}
    </div>
  );
};

export type PaymentViewHeaderProps = TaskHeaderProps & {
  allowSave?: boolean;
  titleActionsConfig?: ActionsDropdownConfig;
  caseId?: string;
};

export const PaymentViewHeader: React.FC<PaymentViewHeaderProps> = ({
  className,
  title,
  allowSave,
  titleActionsConfig,
  caseId,
}) => {
  const [id] = useIdParam();
  return (
    <LightPageHeader tall>
      <CancelBackButton className="mr-3" />
      <Render condition={allowSave}>
        <FormSubmitButton>Save</FormSubmitButton>
      </Render>
      {titleActionsConfig && (
        <ActionsDropdown type={DropdownType.Solid} {...titleActionsConfig} />
      )}
    </LightPageHeader>
  );
};

export type SecondaryHeaderProps = PageHeaderProps & {
  parentRoute?: string;
  route?: string;
  editPermissions?: number[];
  status?: string;
  lob?: string;
  subservice?: string;
  duedate?: string;
  claimantName?: string;
  claimantGender?: string;
  claimantDOB?: string;
  claimNumber?: string;
  referralNumber?: string;
  showCaseMoreDetails?: boolean;
  dateOfInjury?: string;
  dateAndTimeRequestReceived?: string;
  dateOfDisability?: string;
};

export const SecondaryHeader: React.FC<SecondaryHeaderProps> = ({
  children,
  title,
  parentRoute,
  className,
}) => {
  return (
    <div
      className={fullClassName(
        'flex-column border-bottom-light page-header bg-light overflow-auto',
        className
      )}
    >
      <div className="secondary-header-title">
        {title ? <h2>{title}</h2> : children}
      </div>
      <SecondaryNavigationPillBar parentRoute={parentRoute} />
    </div>
  );
};

export const SecondaryHeaderV3: React.FC<SecondaryHeaderProps> = ({
  children,
  title,
  status,
  parentRoute,
  className,
  route,
  editPermissions,
}) => {
  const useStyles = makeStyles({
    pencil: {
      marginLeft: 'auto',
    },
  });
  return (
    <div
      className={fullClassName(
        'flex-column border-bottom-light page-header bg-light overflow-auto',
        className
      )}
    >
      <div className="secondary-header-title">
        {title ? <h2>{title}</h2> : children}
        {status && <span className="secondary-header-status">{status}</span>}
      </div>
      <SecondaryNavigationPillBarV3 parentRoute={parentRoute} />
    </div>
  );
};

const iconMap: Record<string, string> = {
  IME: conditions,
  Pharmacy: pharmacy,
  Disability: bodySystem,
  'Health Review': cardiology,
  'Impairment Rating Review': heapSnapdshotLarge,
  'Workers Comp - Peer Review': engineering,
  "Worker's Comp": engineering,
};

export const EditSecondaryHeader: React.FC<SecondaryHeaderProps> = ({
  children,
  title,
  parentRoute,
  className,
  route,
  editPermissions,
}) => {
  const useStyles = makeStyles({
    pencil: {
      marginLeft: 'auto',
    },
  });
  const classes = useStyles();

  return (
    <div
      className={fullClassName(
        'flex-column border-bottom-light page-header bg-light overflow-auto',
        className
      )}
    >
      <div className="secondary-header-title">
        {title ? <h2>{title}</h2> : children}
        <SecureComponent permissions={editPermissions}>
          <TextEditButton className={classes.pencil} url={route} />
        </SecureComponent>
      </div>
      <SecondaryNavigationPillBar parentRoute={parentRoute} />
    </div>
  );
};

export const mapSmallerSubsetForClientStatuses = (
  appointmentStatus: string
) => {
  if (appointmentStatus == 'Pending Scheduling') {
    return 'Submitted';
  } else if (appointmentStatus == 'Cancelled') {
    return 'Cancelled';
  } else if (appointmentStatus == 'Closed') {
    return 'Closed';
  } else {
    return 'Under Review';
  }
};

export const EditSecondaryHeaderV3: React.FC<SecondaryHeaderProps> = ({
  children,
  title,
  parentRoute,
  className,
  route,
  editPermissions,
  status,
  lob,
  subservice,
  duedate,
  claimantName,
  claimantGender,
  claimantDOB,
  claimNumber,
  referralNumber,
  dateOfInjury,
  dateAndTimeRequestReceived,
  dateOfDisability,
  showCaseMoreDetails = false,
}) => {
  const userdata = useGlobalContext()[0]?.currentUser;
  const entity = useEntity();
  const useStyles = makeStyles({
    pencil: {
      marginLeft: 'auto',
    },
  });
  const classes = useStyles();
  const isIRR = lob === 'IRR' || lob === 'Impairment Rating Review';
  const isWorkerComp =
    lob === "Worker's Comp" || lob === 'Workers Comp - Peer Review';
  const isPharmacyOrHR = lob === 'Pharmacy' || lob === 'Health Review';
  const isDisability = lob === 'Disability';
  const isIME = lob === 'IME';

  let caseDateLabel: string | null = null;
  let caseDateValue: string = 'N/A';
  let showDTRRTooltip = false;

  if (isIME) {
    // IME -> DOI/DOD (usa DOI conforme especificação)
    caseDateLabel = 'DOI/DOD';
    caseDateValue = dateOfInjury || 'N/A';
  } else if (isDisability) {
    // Disability -> DOD
    caseDateLabel = 'DOD';
    caseDateValue = dateOfDisability || 'N/A';
  } else if (isPharmacyOrHR) {
    // Pharmacy/Health Review -> DTRR + tooltip
    caseDateLabel = 'DTRR';
    caseDateValue = dateAndTimeRequestReceived || 'N/A';
    showDTRRTooltip = true;
  } else if (isIRR || isWorkerComp) {
    // IRR / Worker Comp -> DOI
    caseDateLabel = 'DOI';
    caseDateValue = dateOfInjury || 'N/A';
  }

  const isClient = userdata?.isClient;
  return (
    <div
      className={fullClassName(
        'flex-column page-header bg-light overflow-auto',
        className
      )}
    >
      <div className="secondary-header-title">
        {title ? <h2>{title}</h2> : children}
        {status ? (
          <StatusPill
            status={
              isClient ? mapSmallerSubsetForClientStatuses(status) : status
            }
          />
        ) : (
          ''
        )}
        {userdata?.isClient &&
        entity?.caseStatus?.displayName === CaseStatus.Closed ? undefined : (
          <SecureComponent permissions={editPermissions}>
            {!userdata?.isClient && (
              <TextEditButton className={classes.pencil} url={route} />
            )}
          </SecureComponent>
        )}
      </div>
      <Row>
        <Col xs="12" sm="6" lg="3">
          <table className="text-left">
            <tbody>
              <tr>
                <td className="lg-1">
                  <h5>Line of Business</h5>
                </td>
              </tr>
              <tr>
                <td
                  className="lg-1"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  {lob && (
                    <img
                      src={iconMap[lob]}
                      alt={lob}
                      style={{ marginRight: 5 }}
                    />
                  )}
                  {lob || 'N/A'}
                </td>
              </tr>
              <tr>
                <td className="lg-1">
                  {subservice} | Due Date: {duedate}
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
        <Col xs="12" sm="6" lg="3">
          <table className="text-left">
            <tbody>
              <tr>
                <td className="lg-1">
                  <h5>Claimant Demographics</h5>
                </td>
              </tr>
              <tr>
                <td className="lg-1">
                  {claimantName} | {claimantGender}
                </td>
              </tr>
              <tr>
                <td className="lg-1">DOB: {claimantDOB}</td>
              </tr>
            </tbody>
          </table>
        </Col>
        <Col xs="12" sm="6" lg="3">
          <table className="text-left">
            <tbody>
              <tr>
                <td className="lg-1">
                  <h5>Claim/Client Details</h5>
                </td>
              </tr>
              <tr>
                <td className="lg-1">
                  Claim: #{claimNumber} | Referral: #{referralNumber}
                </td>
              </tr>
              <tr>
                <td className="lg-1">
                  {caseDateLabel &&
                    (showDTRRTooltip ? (
                      <Tooltip title="Date & Time Request Received - This is the date that the request was received and TAT clock begins">
                        <span>
                          {caseDateLabel}: {caseDateValue}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>
                        {caseDateLabel}: {caseDateValue}
                      </span>
                    ))}
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Row>
      <SecondaryNavigationPillBarV3 parentRoute={parentRoute} />
    </div>
  );
};

export const ViewHeader: React.FC<TaskHeaderProps> = ({
  children,
  title,
  customButtons,
  tall,
  className,
}) => {
  return (
    <LightPageHeader tall={tall}>
      <div className="flex-grow">
        {title && <h1>{title}</h1>}
        {children}
      </div>
      {customButtons || (
        <Fragment>
          <CancelBackButton className="mr-3">Back</CancelBackButton>
        </Fragment>
      )}
    </LightPageHeader>
  );
};
